<template>
  <div class="people-detail-view page" :class="pageCollection">
    <div class="page__inner">
      <AppPageLoader :collection="pageCollection" @loaded="onPageLoaded">
        <LayoutsScrollMarqueeBackground :title="state.data.title" />

        <LayoutsPageHeaderSimple :title="state.data.title" />

        <AppTagButtonList v-if="state.data.belongs_to" :data="state.data.belongs_to" class="people-detail-view__tag-button-list" />

        <section
          v-if="state.persons && state.persons.length !== 0"
          class="people-detail-view__section"
        >
          <ol class="people-detail-view__list">
            <li
              v-for="(person, index) in state.persons"
              :key="`person_${index}`"
              class="people-detail-view__item"
            >
              <AppPersonCard :data="person" />
            </li>
          </ol>
        </section>

        <AppPaginator
          :item-count="state.total || 0"
          :items-per-page="itemsPerPage"
          :initial-page="state.page"
          @on-page-changed="onPaginatorUpdate"
        />
      </AppPageLoader>
    </div>
    <AppAnimatedTitle />
    <LayoutsFooter />
  </div>
</template>

<script setup lang="ts">

import { reactive, watch } from 'vue'
import { useRoute, useRouter } from 'nuxt/app'
import { TPeoplePage } from '~/types/page-types'
import { getCollection } from '~/services/custom-endpoints'
import { TPersonItem } from '~/types/block-types'

const pageCollection = 'people_page'
const itemsPerPage = -1
let filter = {}
const fields = ['*', 'domain.*.*']
const route = useRoute()
const router = useRouter()

type TState = {
  data?: TPeoplePage;
  persons?: TPersonItem[],
  total?: number,
  page: number,
};

const state: TState = reactive({
  data: undefined,
  persons: undefined,
  total: undefined,
  page: (route.query.p) ? parseInt(route.query.p as string) : 1
})

const onPageLoaded = async (data) => {
  state.data = data

  if (state.data?.persons.length > 0) {
    await loadPersonCards()
  }
}

const setQueryParams = () => {
  const query = (state.page > 1) ? { p: state.page } : undefined

  router.push({
    path: router.currentRoute.value.path,
    query
  })
}

const loadPersonCards = async () => {
  const personPageIds = state.data?.persons?.map((item) => {
    return item.person_page_id
  })
  filter = { id: { _in: personPageIds } }

  state.persons = undefined

  const { data: result, error } = await getCollection(
    'person_page',
    filter,
    fields,
    undefined,
    itemsPerPage,
    state.page,
    true
  )

  if (error.value) {
    throw new Error(error.value?.message)
  }

  if (result.value?.data && personPageIds) {
    state.total = result.value.meta.filter_count
    // sort results by the original id array order
    state.persons = result.value.data.sort(function (a, b) {
      return personPageIds.indexOf(a.id) - personPageIds.indexOf(b.id)
    }) as TPersonItem[]
    setQueryParams()
  }
}

const onPaginatorUpdate = (pageNumber) => {
  window.scrollTo(0, 0)
  state.page = pageNumber
}

watch(() => state.page, loadPersonCards)

</script>

<style scoped lang="postcss">
.people-detail-view {
  &__tag-button-list {
    display: flex;
    justify-content: center;
    padding-bottom: 3rem;
    padding-top: 1rem;
  }

  &__section {
    @mixin container;
    @mixin app-container-padding;
    @mixin app-grid;
  }

  &__list {
    @mixin app-layout-overview-grid;
  }

  &__item {
    display: flex;
    width: 100%;
    justify-content: center;
    padding-bottom: 1rem;

    @media (--md) {
      padding-bottom: 2rem;
    }

    @media (--xl) {
      padding-bottom: 3rem;
    }
  }
}

/*  */
</style>
